/**
 * Conditionally joins class names together.
 * Accepts string, false, null, or undefined values.
 * Filters out falsy values and joins remaining strings with spaces.
 *
 * IMPORTANT: This function does NOT accept arrays of strings.
 * Each argument must be a single string or a conditional expression that returns a string.
 *
 * @example
 * // ✅ CORRECT - Single strings and conditionals
 * cn('base-class', isActive && 'active-class', 'another-class')
 * // Returns: "base-class active-class another-class" (if isActive is true)
 *
 * @example
 * // ✅ CORRECT - Multiple conditionals
 * cn(
 *   'btn',
 *   variant === 'primary' && 'bg-primary-500 text-white',
 *   variant === 'secondary' && 'bg-gray-200 text-gray-800',
 *   size === 'large' && 'px-6 py-3 text-lg',
 *   disabled && 'opacity-50 cursor-not-allowed'
 * )
 *
 * @example
 * // ❌ INCORRECT - Arrays are NOT supported
 * cn('base', [
 *   'class1',
 *   'class2'
 * ]) // TypeScript error: Type 'string[]' is not assignable
 *
 * @example
 * // ✅ CORRECT - Flatten arrays into single strings
 * cn(
 *   'base',
 *   condition && 'class1 class2 class3'
 * )
 *
 * @param classes - Variable number of class name arguments (strings or falsy values)
 * @returns A single string with all truthy class names joined by spaces
 */
export function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}

// Trigger a browser download for a given Blob
export function downloadBlobAsFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// Safe extraction of an error message from unknown error-like values
export function getErrorMessage(err: unknown, fallback = 'Unexpected error'): string {
  if (typeof err === 'string') return err
  if (err && typeof err === 'object') {
    const obj = err as Record<string, unknown>
    // Axios-style: err.response?.data?.message
    const response = obj['response']
    if (response && typeof response === 'object') {
      const data = (response as Record<string, unknown>)['data']
      if (data && typeof data === 'object') {
        const msg = (data as Record<string, unknown>)['message']
        if (typeof msg === 'string') return msg
      }
    }
    const msg = obj['message']
    if (typeof msg === 'string') return msg
  }
  return fallback
}
